import { useState } from "react";
import { Link2, Check } from "lucide-react";
import { useArtworkForm, useArtwork } from "@design-lab/entities/artwork";
import { useCurrentComposition } from "@design-lab/entities/composition";
import { useProductColors } from "@design-lab/entities/product";
import { useIsStaff } from "@design-lab/shared/lib/config";

export function ShareDesignLink() {
  const isStaff = useIsStaff();
  const [copied, setCopied] = useState(false);
  const { form } = useArtworkForm();
  const { getArtwork } = useArtwork();
  const { currentProduct } = useCurrentComposition();
  const { selectedColor } = useProductColors();

  const handleClick = async () => {
    const frontArtworkId = form.watch("front.artworkId");
    const backArtworkId = form.watch("back.artworkId");

    if ((!frontArtworkId && !backArtworkId) || !currentProduct || !selectedColor) {
      return;
    }

    try {
      // Get artwork data with URLs
      const frontArtwork = frontArtworkId ? getArtwork(frontArtworkId) : null;
      const backArtwork = backArtworkId ? getArtwork(backArtworkId) : null;

      // Build URL with Design Lab parameters
      const url = new URL("/design-lab", window.location.origin);
      url.searchParams.set("catalog_number", currentProduct.catalogNumber);
      url.searchParams.set("ac", selectedColor.name);

      // Add artwork URLs for front and/or back using the new upload_urls format
      // Key is the side (front/back), value is the URL
      const uploadArtworkUrls: Record<string, string> = {};

      if (frontArtwork?.originalImageUrl) {
        uploadArtworkUrls["front"] = frontArtwork.originalImageUrl;
      }

      if (backArtwork?.originalImageUrl) {
        uploadArtworkUrls["back"] = backArtwork.originalImageUrl;
      }

      if (Object.keys(uploadArtworkUrls).length > 0) {
        url.searchParams.set("upload_urls", JSON.stringify(uploadArtworkUrls));
        url.searchParams.set("file_content_type", "image");
      }

      await navigator.clipboard.writeText(url.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("[SHAREABLE-LINK] Error generating shareable link:", error);
      window.Honeybadger?.notify("Error generating shareable link", {
        params: {
          error: error instanceof Error ? error.message : String(error),
          error_object: error instanceof Error ? error.toString() : String(error),
          stack_trace: error instanceof Error ? error.stack : null,
          component: "ShareDesignLink"
        }
      });
    }
  };

  const frontArtworkId = form.watch("front.artworkId");
  const backArtworkId = form.watch("back.artworkId");

  // Get artwork data to check if URLs are available
  const frontArtwork = frontArtworkId ? getArtwork(frontArtworkId) : null;
  const backArtwork = backArtworkId ? getArtwork(backArtworkId) : null;

  // Only show shareable link if at least one artwork URL is available
  const hasArtworkUrl = Boolean(
    (frontArtwork?.originalImageUrl || backArtwork?.originalImageUrl) &&
    currentProduct &&
    selectedColor
  );

  if (!hasArtworkUrl || !isStaff) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="share-design-link-button"
      className="group relative cursor-pointer"
      aria-label="Copy shareable link"
      title="Copy shareable link"
    >
      {copied ? (
        <Check className="size-6 text-green-600 lg:size-8" />
      ) : (
        <Link2 className="size-6 lg:size-8" />
      )}

      {/* Tooltip */}
      <span className="pointer-events-none absolute -bottom-10 right-0 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
        {copied ? "Link copied!" : "Copy shareable link"}
      </span>
    </button>
  );
}
